import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Stack, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, Alert, CircularProgress, Tooltip, Divider, Autocomplete, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { UploadOutlined, CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import axios from 'api/axiosInstance';
import endpoints from 'api/endpoints';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function ImportProspects() {
  const navigate = useNavigate();
  const [step, setStep] = useState('upload'); // upload | preview | done
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [catOpen, setCatOpen] = useState(false); // category create/select step
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(endpoints.prospects.categories, { headers: authHeaders() })
      .then((r) => setCategories(r.data?.categories || [])).catch(() => {});
  }, []);

  const reset = () => { setStep('upload'); setFile(null); setRows([]); setSummary(null); setResult(null); setError(''); setCategory(''); };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(xlsx|xls|csv)$/i.test(f.name)) { setError('Please upload a .xlsx or .csv file'); return; }
    setFile(f); setError('');
  };

  const downloadTemplate = async () => {
    try {
      const resp = await axios.get(endpoints.prospects.template, { headers: authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'prospects_import_template.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { setError('Could not download template'); }
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true); setError('');
    try {
      const fd = new FormData(); fd.append('file', file);
      // Do NOT set Content-Type here — let the browser/axios add the correct
      // `multipart/form-data; boundary=…`. Forcing it without a boundary makes
      // the file part fail to serialize, so the server received an empty body
      // ("File is not a zip file").
      const resp = await axios.post(endpoints.prospects.preview, fd, { headers: authHeaders() });
      setRows(resp.data?.rows || []);
      setSummary(resp.data?.summary || null);
      setStep('preview');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not parse the file');
    } finally { setLoading(false); }
  };

  const handleImport = async () => {
    const valid = rows.filter((r) => r.valid);
    if (valid.length === 0) return;
    if (!category.trim()) { setError('Choose or enter a category for this batch'); return; }
    setLoading(true); setError('');
    try {
      const resp = await axios.post(endpoints.prospects.import, { rows: valid, category: category.trim().slice(0, 40) }, { headers: authHeaders() });
      setResult(resp.data);
      setCatOpen(false);
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed');
    } finally { setLoading(false); }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', py: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>Import Prospects</Typography>
        <Stack direction="row" spacing={1}>
          <Button onClick={() => navigate('/prospects')}>View prospects</Button>
          {step !== 'upload' && <Button startIcon={<ReloadOutlined />} onClick={reset}>Start over</Button>}
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {step === 'upload' && (
        <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload an <strong>.xlsx</strong> or <strong>.csv</strong> with columns:
            <br />Company name, Admin name, Email, Phone, Place, Industry type. Only <strong>Company name</strong> is required.
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Button variant="text" startIcon={<DownloadOutlined />} onClick={downloadTemplate}>Download sample template</Button>
          </Box>
          <Button variant="outlined" component="label" startIcon={<UploadOutlined />} sx={{ mb: 1.5 }}>
            Choose file
            <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} />
          </Button>
          {file && <Typography variant="body2" sx={{ mb: 2 }}>📄 {file.name}</Typography>}
          <Box>
            <Button variant="contained" disabled={!file || loading} onClick={handlePreview}>
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Preview'}
            </Button>
          </Box>
        </Paper>
      )}

      {step === 'preview' && (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip color="success" label={`${summary?.valid || 0} valid`} />
              {summary?.invalid > 0 && <Chip color="error" label={`${summary.invalid} skipped`} />}
              <Chip variant="outlined" label={`${summary?.total || 0} total`} />
            </Stack>
            <Button variant="contained" size="large" disabled={!(summary?.valid)} onClick={() => { setError(''); setCatOpen(true); }}>
              Save {summary?.valid || 0} prospect{summary?.valid === 1 ? '' : 's'} →
            </Button>
          </Box>
          <Divider />
          <Box sx={{ overflowX: 'auto', maxHeight: 460 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell><TableCell>Company</TableCell><TableCell>Admin</TableCell>
                  <TableCell>Email</TableCell><TableCell>Phone</TableCell><TableCell>Place</TableCell><TableCell>Industry</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.slice(0, 150).map((r, i) => (
                  <TableRow key={i} sx={{ bgcolor: r.valid ? 'inherit' : '#FEF2F2' }}>
                    <TableCell>
                      {r.valid ? <CheckCircleOutlined style={{ color: '#15803D' }} />
                        : <Tooltip title={r.errors?.join(', ')}><CloseCircleOutlined style={{ color: '#DC2626' }} /></Tooltip>}
                    </TableCell>
                    <TableCell>{r.company_name || '—'}</TableCell>
                    <TableCell>{r.admin_name || '—'}</TableCell>
                    <TableCell>{r.email || '—'}</TableCell>
                    <TableCell>{r.phone || '—'}</TableCell>
                    <TableCell>{r.place || '—'}</TableCell>
                    <TableCell>{r.industry || '—'}</TableCell>
                  </TableRow>
                ))}
                {rows.length > 150 && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ color: 'text.secondary', py: 1.5 }}>
                    …showing first 150 of {rows.length} rows. All {summary?.valid || 0} valid rows will be saved.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}

      {/* Category create/select step */}
      <Dialog open={catOpen} onClose={() => setCatOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Choose a category</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These {summary?.valid || 0} prospects will be grouped under this category for the marketing team. Pick an existing one or type a new one.
          </Typography>
          <Autocomplete
            freeSolo options={categories} value={category}
            onChange={(_, v) => setCategory((v || '').slice(0, 40))}
            onInputChange={(_, v) => setCategory((v || '').slice(0, 40))}
            renderInput={(params) => <TextField {...params} autoFocus label="Category (max 40 chars)" placeholder="e.g. Kollam Homeo · Aug" />}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatOpen(false)} disabled={loading}>Cancel</Button>
          <Button variant="contained" disabled={loading || !category.trim()} onClick={handleImport}>
            {loading ? <CircularProgress size={20} color="inherit" /> : `Save ${summary?.valid || 0} prospects`}
          </Button>
        </DialogActions>
      </Dialog>

      {step === 'done' && result && (
        <Stack spacing={2}>
          <Alert severity="success">
            Added <strong>{result.created}</strong> prospect(s) under category <strong>{result.category || '(none)'}</strong>.
          </Alert>
          <Stack direction="row" spacing={1.5}>
            <Button variant="contained" onClick={() => navigate('/prospects')}>View prospects</Button>
            <Button onClick={reset}>Import another file</Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
}
